import React, { useState, useEffect } from 'react';
// Fixed: Ensure showPersonalization state is properly defined
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { categoryConfigs } from '@/config/categories';
import { backendClient } from '@/lib/backendClient';
import { useToast } from '@/hooks/use-toast';
import { QuestionnaireData } from '@/types/category';
import { GroupSessionScope } from '@/types/groupSession';
import { extractUserVariables, generateTemplatePrompt } from '@/utils/templateProcessor';
import { parseBiographyVariables } from '@/utils/biographyParser';
import { useZenAfContentValidator } from '@/hooks/useZenAfContentValidator';
import { createDeeplyPersonalizedContent, createCategorySpecificBiographyIntegration } from '@/utils/comprehensiveBiographyIntegration';
import { createNaturalBiographyWeaving } from '@/utils/advancedBiographyWeaving';
import { validateBiographyQuality, optimizeBiographyForDeepIntegration } from '@/utils/smartBiographyOptimization';
import { createEnhancedZenAfPrompt, createEnhancedCategoryPrompt } from '@/utils/enhancedPromptGeneration';
import { processSessionLengthPlaceholders } from '@/utils/sessionLengthProcessor';
import { useOptimizedUserPreferences } from '@/hooks/useOptimizedUserPreferences';
import { usePractitionerCredits } from '@/hooks/usePractitionerCredits';
import { usePricingRates } from '@/hooks/usePricingRates';
import { useCreditWarnings } from '@/hooks/useCreditWarnings';
import { getBackgroundMusicUrl } from '@/hooks/useBackgroundMusic';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Play, ArrowLeft, Loader2, Sparkles, User, Settings, Newspaper, Coins } from 'lucide-react';
import EnhancedAudioPlayer from '@/components/EnhancedAudioPlayer';
import { ZenAfQuickDebug } from '@/components/ZenAfQuickDebug';
import SimpleAudioFallback from '@/components/SimpleAudioFallback';

import BackgroundAudioControls from '@/components/BackgroundAudioControls';
import CategorySpecificPersonalizationQuestionnaire from '@/components/CategorySpecificPersonalizationQuestionnaire';
import BiographyStep from '@/components/BiographyStep';
import GroupSessionSelector from '@/components/group/GroupSessionSelector';
import { SessionTypeSelector } from '@/components/SessionTypeSelector';
import MeditativeBackground from '@/components/MeditativeBackground';
import { useCategoryTheme } from '@/contexts/CategoryThemeContext';
import { SignInPrompt } from '@/components/SignInPrompt';

import RoleBasedCategoryAccess from '@/components/RoleBasedCategoryAccess';

// Queue Status Display
import { useAudioGenerationWithQueue } from '@/hooks/useAudioGenerationWithQueue';
import QueueStatusCard from '@/components/QueueStatusCard';

// Async Meditation API with global job tracking
import { useMeditationGeneration } from '@/hooks/useMeditationGeneration';
import { useVoices } from '@/hooks/useVoices';
import { validateVoiceSelection } from '@/utils/voiceValidation';

// Get category-specific icon color using CSS variables (matching sidebar colors)
const getCategoryIconColor = (categoryId: string): string => {
  const colorMap: Record<string, string> = {
    'meditation': 'text-[hsl(var(--category-meditation))]',
    'anxiety': 'text-[hsl(var(--category-anxiety))]',
    'massage': 'text-[hsl(var(--category-massage))]',
    'sleep': 'text-[hsl(var(--category-sleep))]',
    'self_love': 'text-[hsl(var(--category-self_love))]',
    'fitness': 'text-[hsl(var(--category-fitness))]',
    'life_celebrations': 'text-[hsl(var(--category-life_celebrations))]',
    'motivation': 'text-[hsl(var(--category-motivation))]',
    'memory': 'text-[hsl(var(--category-memory))]',
    'be_still': 'text-[hsl(var(--category-be_still))]',
    'news_meditation': 'text-[hsl(var(--category-news_meditation))]',
    'zen-af': 'text-[hsl(var(--category-zen-af))]',
    'yoga': 'text-[hsl(var(--category-yoga))]',
    'couples-connection': 'text-[hsl(var(--category-couples-connection))]',
    'addiction-recovery': 'text-[hsl(var(--category-addiction-recovery))]',
    'relationship-decisions': 'text-[hsl(var(--category-relationship-decisions))]',
    'sex-positive': 'text-[hsl(var(--category-sex-positive))]',
  };
  
  return colorMap[categoryId] || 'text-muted-foreground';
};


const CleanCategoryPage = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const {
    updatePreference,
    getBackgroundMusicSelection
  } = useOptimizedUserPreferences();

  // Credit and pricing hooks
  const { credits, totalAvailableCredits } = usePractitionerCredits();
  const { audioRate } = usePricingRates();
  const { checkBeforeAction, warnBeforeAction } = useCreditWarnings();

  // ZEN AF content validation hook
  const { validateAndRegenerateIfNeeded } = useZenAfContentValidator();

  // Queue Status Display hook
  const queueStatus = useAudioGenerationWithQueue();

  // Voice validation
  const { voices } = useVoices();

  // Meditation generation with global job tracking
  const { generate: generateMeditationAsync } = useMeditationGeneration();

  const [category, setCategory] = useState<any>(null);
  const [biography, setBiography] = useState('');
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [sessionTitle, setSessionTitle] = useState('');
  const [currentBackgroundMusicFile, setCurrentBackgroundMusicFile] = useState<string | undefined>(undefined);
  const [quickNewsInput, setQuickNewsInput] = useState<string>('');
  const [quickNewsCharCount, setQuickNewsCharCount] = useState<number>(0);
  const [profile, setProfile] = useState<any>(null);
  const [currentSessionType, setCurrentSessionType] = useState<string | null>(null);
  const [useAudioFallback, setUseAudioFallback] = useState(false);
  const [audioPlayerError, setAudioPlayerError] = useState<string | null>(null);
  
  const [showPersonalization, setShowPersonalization] = useState(false);
  const [showBiographyStep, setShowBiographyStep] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [sessionTypeSelected, setSessionTypeSelected] = useState(false);
  const [selectedSessionType, setSelectedSessionType] = useState<'individual' | 'group'>('individual');
  const [generatedSessionId, setGeneratedSessionId] = useState<string | null>(null);

  // Debug: Prompt preview
  const [lastPrompt, setLastPrompt] = useState<string>('');
  const [showPromptPreview, setShowPromptPreview] = useState<boolean>(false);
  
  const [groupSessionScope, setGroupSessionScope] = useState<GroupSessionScope>(() => {
    // Restore group session state from localStorage
    const saved = localStorage.getItem('groupSessionScope');
    console.log('Restoring group session state:', saved);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        console.log('Parsed state:', parsed);
        return parsed;
      } catch (error) {
        console.error('Error parsing saved group session state:', error);
        return { sessionScope: 'individual' };
      }
    }
    console.log('No saved state, defaulting to individual');
    return { sessionScope: 'individual' };
  });

  // Persist group session state to localStorage whenever it changes
  useEffect(() => {
    console.log('Saving group session state:', groupSessionScope);
    localStorage.setItem('groupSessionScope', JSON.stringify(groupSessionScope));
  }, [groupSessionScope]);


  // Debug effect to track audio state changes
  useEffect(() => {
    console.log('🎵 Audio State Change:', {
      audioUrl: audioUrl ? 'SET' : 'NULL',
      sessionTitle: sessionTitle || 'EMPTY',
      currentSessionType: currentSessionType || 'NONE',
      timestamp: new Date().toISOString()
    });
  }, [audioUrl, sessionTitle, currentSessionType]);

  // State reset function for fresh category navigation
  const resetPageState = () => {
    setShowPersonalization(false);
    setShowBiographyStep(false);
    setSessionTypeSelected(false);
    setAudioUrl(null);
    setSessionTitle('');
    setIsGenerating(false);
    setCurrentSessionType(null);
    setLastPrompt('');
    setShowPromptPreview(false);
    setQuickNewsInput('');
    setQuickNewsCharCount(0);
    setAudioPlayerError(null);
    setUseAudioFallback(false);
  };

  // Load category without applying page-wide theme
  useEffect(() => {
    const foundCategory = categoryConfigs[categoryId!];
    if (foundCategory) {
      console.log('🎯 Category loaded:', categoryId, {
        hasDefaultPrompt: !!foundCategory.defaultPrompt,
        hasSamplePrompt: !!foundCategory.samplePrompt,
        prompts: {
          defaultPrompt: foundCategory.defaultPrompt?.substring(0, 100) + '...',
          samplePrompt: foundCategory.samplePrompt?.substring(0, 100) + '...'
        }
      });
      
      // Reset all page state for fresh category experience
      resetPageState();
      setCategory(foundCategory);
      // Keep golden theme for all pages - no category-specific page theming
    } else {
      console.error('❌ Category not found:', categoryId);
      navigate('/');
    }
  }, [categoryId, navigate]);

  // Load user biography and profile
  useEffect(() => {
    if (user) {
      loadBiography();
      loadProfile();
    }
  }, [user]);

  const loadBiography = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await backendClient
        .from('profiles')
        .select('biography')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      setBiography(data?.biography || '');
    } catch (error) {
      console.error('Error loading biography:', error);
    }
  };

  const loadProfile = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await backendClient
        .from('profiles')
        .select('first_name, preferred_name, name_usage_preference')
        .eq('id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data) {
        setProfile(data);
      } else {
        // Fallback to user metadata
        setProfile({
          first_name: user.user_metadata?.first_name || '',
          preferred_name: user.user_metadata?.preferred_name || '',
          name_usage_preference: user.user_metadata?.name_usage_preference || 'first',
        });
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      // Fallback to user metadata on error
      setProfile({
        first_name: user.user_metadata?.first_name || '',
        preferred_name: user.user_metadata?.preferred_name || '',
        name_usage_preference: user.user_metadata?.name_usage_preference || 'first',
      });
    }
  };

  const pollForJobCompletion = async (jobId: string, maxAttempts = 30): Promise<any> => {
    console.log(`🔄 Starting to poll for job ${jobId}`);
    
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        // Wait 2 seconds between polls (first wait, then check)
        if (attempt > 1) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
        
        // Check job status
        const { data: job, error } = await backendClient
          .from('jobs')
          .select('status, result_data, error_message, progress')
          .eq('id', jobId)
          .single();
        
        if (error) {
          console.error(`❌ Error polling job ${jobId}:`, error);
          continue; // Try again
        }
        
        console.log(`📊 Job ${jobId} status:`, job?.status, `Progress: ${job?.progress || 0}%`);
        
        if (job?.status === 'COMPLETED' || job?.status === 'completed') {
          console.log(`✅ Job ${jobId} completed!`);
          // The result_data should contain the meditation content
          return job.result_data;
        } else if (job?.status === 'FAILED' || job?.status === 'failed') {
          console.error(`❌ Job ${jobId} failed:`, job.error_message);
          throw new Error(job.error_message || 'Job failed');
        }
        
        // Update UI with progress if you want
        if (job?.progress) {
          toast({
            title: "Generating...",
            description: `Progress: ${Math.round(job.progress * 100)}%`,
            duration: 1000,
          });
        }
      } catch (error) {
        console.error(`❌ Poll attempt ${attempt} failed:`, error);
        if (attempt === maxAttempts) throw error;
      }
    }
    
    throw new Error('Job polling timed out after ' + maxAttempts + ' attempts');
  };

  const generateAudio = async (prompt: string, title: string, useBiography?: string, preferences?: QuestionnaireData) => {
    if (!user || !category) return;

    // Reset sessionId to clear previous session before generating new one
    setGeneratedSessionId(null);

    // Debug: Log session path being taken
    console.log('🎯 SESSION PATH DEBUG:', {
      sessionScope: groupSessionScope.sessionScope,
      hasGroupSession: !!groupSessionScope.groupSession,
      hasBiography: !!useBiography,
      groupName: groupSessionScope.groupSession?.group_name,
      willUsePersonalBiography: groupSessionScope.sessionScope === 'individual' && !!useBiography
    });

    // Add group session context to the title and session record
    let actualTitle = title;
    let actualSessionType: string = 'personalized';
    if (groupSessionScope.sessionScope === 'group' && groupSessionScope.groupSession) {
      actualTitle = `${title} - ${groupSessionScope.groupSession.group_name}`;
      actualSessionType = 'group';
      console.log('📋 GROUP SESSION: Will use group data, NOT personal biography');
    } else if (groupSessionScope.sessionScope === 'individual') {
      console.log('👤 INDIVIDUAL SESSION: Can use personal biography if available');
    }

    // Check for personalized template (medium priority - minimal tokens used)
    if (category.personalizedTemplate) {
      const variables = extractUserVariables(useBiography || '');
      const templatePrompt = generateTemplatePrompt(
        category.personalizedTemplate,
        variables,
        category.name
      );
      
      setCurrentSessionType('template-personalized');
      setSessionTitle(category.personalizedTemplateTitle || `Personalized ${category.name}`);
      
      // Generate audio from template (uses minimal tokens)
      await generateFromTemplate(templatePrompt, category.personalizedTemplateTitle || `Personalized ${category.name}`, useBiography, preferences);
      return;
    }

  // Enhanced deep personalization with biography quality checking (INDIVIDUAL SESSIONS ONLY)
    // IMPORTANT: Only include biography content if usePersonalBiography toggle is ON
    if (useBiography && !category.personalizedTemplate && groupSessionScope.sessionScope !== 'group' && (preferences?.usePersonalBiography ?? true)) {
      const variables = parseBiographyVariables(useBiography);
      
      // Validate biography quality first
      const qualityCheck = validateBiographyQuality(useBiography, categoryId || '');
      
      if (qualityCheck.qualityScore < 40) {
        // Biography quality is too low for deep personalization
        toast({
          title: "Biography needs more details",
          description: `Your biography score: ${qualityCheck.qualityScore}/100. Add more personal details for better personalization.`,
          variant: "destructive"
        });
        
        // Show biography quality warning but continue with standard questionnaire
        console.log("Biography quality too low for deep personalization, showing standard questionnaire");
        return;
      }
      
      // Optimize biography for better integration
      let optimizedBiography = useBiography;
      try {
        optimizedBiography = await optimizeBiographyForDeepIntegration(useBiography, categoryId || '', 'personalized');
      } catch (error) {
        console.warn('Biography optimization failed, using original:', error);
      }
      
      // Re-parse variables from optimized biography
      const optimizedVariables = parseBiographyVariables(optimizedBiography);
      
      // Create deeply personalized content using advanced biography integration
      const deepPersonalizationPrompt = createDeeplyPersonalizedContent(
        optimizedVariables,
        category.name,
        'meditation session'
      );
      
      // Add natural biography weaving patterns
      const weavingPattern = createNaturalBiographyWeaving(
        optimizedVariables,
        10, // estimated session length in minutes
        category.name
      );
      
      // Create category-specific integration
      const categorySpecificIntegration = createCategorySpecificBiographyIntegration(
        optimizedVariables,
        category.name
      );
      
      // Combine for maximum personalization
      const enhancedPrompt = `${prompt}

DEEP PERSONALIZATION INSTRUCTIONS:
${deepPersonalizationPrompt}

NATURAL WEAVING PATTERNS:
${weavingPattern}

CATEGORY-SPECIFIC INTEGRATION:
${categorySpecificIntegration}

BIOGRAPHY QUALITY SCORE: ${qualityCheck.qualityScore}/100

CRITICAL INSTRUCTIONS:
- Use specific names, places, and personal details from the biography
- Make this feel like it was created specifically for this individual
- Reference their actual life context, relationships, and experiences
- Weave personal elements naturally throughout the entire session
- Address their specific stressors, goals, and relationships by name
- Create a truly personalized experience that couldn't be for anyone else`;
      
      setCurrentSessionType('deeply-personalized');
      setSessionTitle(`Deeply Personalized ${category.name} (Quality: ${qualityCheck.qualityScore}%)`);
      
      // Start generation process with deep personalization
      // No need to set loading state here as it's handled by the calling function
      
      try {
        {/* ZEN AF Debug Panel */}
        {categoryId === 'zen-af' && (
          <ZenAfQuickDebug />
        )}

      setLastPrompt(enhancedPrompt); // Store for debug preview
      console.log('🚀 Generated prompt for:', category.id);
      console.log('📝 Enhanced prompt preview:', enhancedPrompt.substring(0, 200) + '...');
      
      // Generate content using AI with deep personalization
        const isZenAF = categoryId === 'zen-af' || categoryId === 'news-meditation';
        let finalPrompt = enhancedPrompt;

        if (isZenAF) {
          console.log('🧘‍♀️ Applying ZEN AF therapeutic sanitization...');
          const { createTherapeuticZenAfPrompt } = await import('@/utils/zenAfContentSanitizer');
          finalPrompt = createTherapeuticZenAfPrompt(`ZEN AF EXPLICIT THERAPEUTIC SESSION - ${enhancedPrompt}`);
          console.log('✅ ZEN AF content sanitized for therapeutic use');
        }

        // Validate voice selection before generation
        const allVoices = [...(voices?.male || []), ...(voices?.female || [])];
        const voiceValidation = validateVoiceSelection(
          preferences?.specific_voice_id,
          preferences?.voiceGender,
          allVoices
        );

        if (!voiceValidation.canSubmit) {
          toast({
            title: "Invalid Voice Selection",
            description: voiceValidation.errors.join(', '),
            variant: "destructive"
          });
          console.error('🎤 Voice validation failed:', voiceValidation);
          setIsGenerating(false);
          return; // BLOCK SUBMISSION
        }

        const { data: contentData, error: contentError } = await generateMeditationAsync({
          // Required fields for MeditationRequest
          category_id: categoryId!,
          form_type: categoryId!,
          title: preferences?.sessionTitle || undefined, // User-specified session title
          environment: preferences?.environment || 'peaceful setting',
          voice_gender: preferences?.voiceGender || 'female',
          specific_voice_id: preferences?.specific_voice_id || null,
          meditation_style: preferences?.preferredStyle || category.name.toLowerCase(),
          voice_style: preferences?.voiceType || 'calm',
          seeking_goal: preferences?.primaryGoal || 'deep relaxation',
          session_length: preferences?.sessionLength || '5 minutes',
          desired_feeling: preferences?.desiredFeeling || 'peaceful and centered',

          // Optional fields
          words_to_avoid: preferences?.triggers || '',
          include_background_sounds: preferences?.backgroundSounds ?? true,
          background_music_file: preferences?.backgroundMusicSelection || null,

          // Extended questionnaire data - send ALL questionnaire fields
          questionnaire_data: preferences || {},

          // Personal information from questionnaire - only include if toggle is ON
          first_name: (preferences?.usePersonalBiography ?? true) ? (preferences?.firstName || profile?.first_name || user?.user_metadata?.first_name) : undefined,
          last_name: (preferences?.usePersonalBiography ?? true) ? (preferences?.lastName || profile?.last_name || user?.user_metadata?.last_name) : undefined,
          // Personal details only included if usePersonalBiography is ON
          age: (preferences?.usePersonalBiography ?? true) ? preferences?.age : undefined,
          occupation: (preferences?.usePersonalBiography ?? true) ? preferences?.occupation : undefined,
          relationship: (preferences?.usePersonalBiography ?? true) ? preferences?.relationship : undefined,
          location: (preferences?.usePersonalBiography ?? true) ? preferences?.location : undefined,
          hobbies: (preferences?.usePersonalBiography ?? true) ? preferences?.hobbies : undefined,
          stressors: (preferences?.usePersonalBiography ?? true) ? preferences?.stressors : undefined,
          goals: (preferences?.usePersonalBiography ?? true) ? preferences?.goals : undefined,
          biography: (preferences?.usePersonalBiography ?? true) ? (preferences?.biography || useBiography) : undefined,

          // Category-specific data (extract category-specific fields based on category)
          category_specific_data: {
            // Yoga fields
            experienceLevel: preferences?.experienceLevel,
            yogaStyle: preferences?.yogaStyle,
            physicalLimitations: preferences?.physicalLimitations,
            beginningPosition: preferences?.beginningPosition,
            endingPosition: preferences?.endingPosition,
            focusAreas: preferences?.focusAreas,
            breathWork: preferences?.breathWork,
            sessionIntention: preferences?.sessionIntention,
            // Sleep fields
            sessionType: preferences?.sessionType,
            storyAudience: preferences?.storyAudience,
            storyTheme: preferences?.storyTheme,
            includePersonalDetails: preferences?.includePersonalDetails,
            // Massage fields
            massageType: preferences?.massageType,
            targetBodyParts: preferences?.targetBodyParts,
            currentState: preferences?.currentState,
            // News Meditation fields
            currentNews: preferences?.currentNews,
            newsConsumption: preferences?.newsConsumption,
            explicitLanguageLevel: preferences?.explicitLanguageLevel,
            humorLevel: preferences?.humorLevel,
            // Zen AF fields
            groupFrustration: preferences?.groupFrustration,
            deeperReason: preferences?.deeperReason,
            // Addiction Recovery fields
            recoveryStage: preferences?.recoveryStage,
            primaryFocus: preferences?.primaryFocus,
            emotionalNeeds: preferences?.emotionalNeeds,
            // Relationship Decisions fields
            decisionStage: preferences?.decisionStage,
            primaryConcern: preferences?.primaryConcern,
            valuesExploration: preferences?.valuesExploration,
            emotionalState: preferences?.emotionalState,
            supportNeeded: preferences?.supportNeeded,
            sessionApproach: preferences?.sessionApproach,
            // Sexual Wellness fields
            healingApproach: preferences?.healingApproach,
            currentChallenges: preferences?.currentChallenges,
            emotionalTone: preferences?.emotionalTone,
            // Life Celebrations fields
            celebrationType: preferences?.celebrationType,
            celebrationFocus: preferences?.celebrationFocus,
            energyLevel: preferences?.energyLevel,
            specialPeople: preferences?.specialPeople,
          },

          // Custom fields
          prompt: finalPrompt,
          usePersonalBiography: preferences?.usePersonalBiography ?? true,
          allowedNames: (preferences?.usePersonalBiography ?? true) ? {
            firstName: profile?.first_name || user?.user_metadata?.first_name,
            preferredName: profile?.preferred_name || user?.user_metadata?.preferred_name,
            usage: profile?.name_usage_preference || user?.user_metadata?.name_usage_preference || 'first'
          } : undefined
        }, {
          onProgress: (progress, message) => {
            console.log(`📊 Generation progress: ${Math.round(progress * 100)}% - ${message}`);
          },
          onJobCreated: (jobId) => {
            console.log('🔍 Job created, starting tracking:', jobId);
            queueStatus.startTracking(jobId);
          }
        });

        if (contentError) {
          console.error('❌ Content generation error:', contentError);
          throw contentError;
        }

        // Debug: Log contentData structure
        console.log('🔍 ContentData received:', contentData);
        console.log('🔍 ContentData.content:', contentData?.content);
        console.log('🔍 ContentData type:', typeof contentData);

        // Validate that we have content before proceeding
        if (!contentData || !contentData.script) {
          console.error('❌ No content received from generate-meditation:', contentData);
          throw new Error('No content generated - please try again');
        }

        console.log('✅ Content validated, length:', contentData.content.length);

        // Audio URL is now included in contentData from async API
        const audioBlobUrl = contentData.audio_url || contentData.mixed_audio_url;
        const mode = contentData.mode || 'audio';

        // Handle service unavailable mode (DLQ)
        if (mode === 'service_unavailable') {
          console.warn('⚠️ Audio service unavailable - job queued for retry');
          toast({
            title: "Script Ready - Audio Pending",
            description: "Audio generation is queued and will retry automatically. Check session history for updates.",
            variant: "default"
          });
          return;
        }

        if (!audioBlobUrl) {
          throw new Error('Audio generation failed - please try again');
        }

      // Check if backend worker already created the session (new architecture)
      let sessionId = contentData?.session_id;

      if (sessionId) {
        // Backend worker already created the session - use that ID
        console.log('✅ Using session created by backend worker:', sessionId);
        setGeneratedSessionId(sessionId);
      } else {
        // Backward compatibility: create session if backend didn't
        console.log('📝 Backend did not create session, creating in frontend (old flow)');
        const actualDuration = preferences?.sessionLength ?
          (typeof preferences.sessionLength === 'string' ? parseInt(preferences.sessionLength.split(' ')[0] || '3') * 60 : preferences.sessionLength * 60) :
          180;
        const { data: session, error } = await backendClient.from('sessions').insert({
          user_id: user.id,
          category_id: categoryId!,
          title: `Deeply Personalized ${category.name}`,
          audio_url: audioBlobUrl,
          session_type: 'deeply-personalized',
          duration: actualDuration,
          session_scope: groupSessionScope.sessionScope,
          group_session_id: groupSessionScope.groupSessionId || null,
          background_music_file: getBackgroundMusicSelection()
        }).select().single();

        if (!error && session) {
          setGeneratedSessionId(session.id);
        }
      }
      setCurrentBackgroundMusicFile(getBackgroundMusicSelection());

        setAudioUrl(audioBlobUrl);
        queueStatus.stopTracking(); // Stop tracking when generation completes
        toast({
          title: "Deeply Personalized Session Generated",
          description: `Created with ${qualityCheck.qualityScore}% biography integration. Your session references your specific life details and experiences.`
        });

      } catch (error: any) {
        console.error('Deep personalization error:', error);
        toast({
          title: "Deep Personalization Failed",
          description: "Falling back to template generation...",
          variant: "destructive"
        });
        
        // Fallback to template or basic generation with personalization
        const variables = extractUserVariables(useBiography || '');
        let fallbackPrompt: string;
        
          if (preferences) {
            // Include user preferences in fallback
            fallbackPrompt = `Create a personalized ${category.name.toLowerCase()} session with these preferences:

PERSONALIZATION PREFERENCES:
- Voice Gender: ${preferences.voiceGender}
- Session Length: ${parseInt(preferences.sessionLength?.split(' ')[0] || '5')} minutes  
- Voice Type: ${preferences.voiceType}
- Primary Goal: ${preferences.primaryGoal}
- Preferred Style: ${preferences.preferredStyle}

${useBiography ? `Background context: ${useBiography}` : ''}

Please create a session that incorporates their preferences and goals.

${category.samplePrompt || ''}`;
          } else {
          // Basic template fallback
          const templatePrompt = generateTemplatePrompt(
            category.personalizedTemplate || `Create a personalized ${category.name.toLowerCase()} session`,
            variables,
            category.name
          );
          fallbackPrompt = templatePrompt;
        }
        
        await generateFromTemplate(fallbackPrompt, `Personalized ${category.name}`, useBiography, preferences);
      } finally {
        // Loading state handled by calling function
      }
      
      return;
    }

    // Start generation process
    setIsGenerating(true);
    setCurrentSessionType(actualSessionType);
    setSessionTitle(title);

    try {
      let finalContent: string;
      let contentData: any = null;
      let contentError: any = null;

      // Check for deep personalization without template (INDIVIDUAL SESSIONS ONLY)
      // IMPORTANT: Only include biography content if usePersonalBiography toggle is ON
      if (useBiography && groupSessionScope.sessionScope !== 'group' && (preferences?.usePersonalBiography ?? true)) {
          const variables = parseBiographyVariables(useBiography);

          // Create deeply personalized content using advanced biography integration
          const deepPersonalizationPrompt = createDeeplyPersonalizedContent(
            variables,
            category.name,
            'meditation session'
          );

          // Add natural biography weaving patterns
          const weavingPattern = createNaturalBiographyWeaving(
            variables,
            10, // estimated session length in minutes
            category.name
          );

          // Enhance the original prompt with deep personalization
          const enhancedPrompt = `${prompt}\n\nDEEP PERSONALIZATION INSTRUCTIONS:\n${deepPersonalizationPrompt}\n\nNATURAL WEAVING PATTERNS:\n${weavingPattern}\n\nCRITICAL: Use specific names, places, and personal details from the biography. Make this feel like it was created specifically for this individual by referencing their actual life context, relationships, and experiences.`;

          prompt = enhancedPrompt;
        }

        // Generate content using AI
        const isZenAF = categoryId === 'zen-af' || categoryId === 'news-meditation';
        const zenAFPrompt = isZenAF ? `ZEN AF EXPLICIT THERAPEUTIC SESSION - ${prompt}` : prompt;

        // Debug group session data transmission BEFORE sending to edge function
        console.log('🚀 PREPARING EDGE FUNCTION CALL:', {
          promptLength: zenAFPrompt.length,
          useBiography: useBiography,
          usePersonalBiography: preferences?.usePersonalBiography ?? true,
          sessionType: actualSessionType,
          groupSessionScope: groupSessionScope,
          hasGroupSession: !!(groupSessionScope.sessionScope === 'group' && groupSessionScope.groupSession),
          groupParticipantsCount: groupSessionScope.groupSession?.participants?.length || 0,
          groupSessionPayload: groupSessionScope.sessionScope === 'group' ? {
            id: groupSessionScope.groupSession?.id,
            name: groupSessionScope.groupSession?.group_name,
            type: groupSessionScope.groupSession?.group_type,
            participantNames: groupSessionScope.groupSession?.participants
              ?.filter(p => p.consentToName)
              ?.map(p => p.preferredName || p.firstName) || []
          } : 'NOT_INCLUDED'
        });

        const response = await generateMeditationAsync({
          // Required fields for MeditationRequest
          category_id: categoryId!,
          form_type: categoryId!,
          title: preferences?.sessionTitle || undefined, // User-specified session title
          environment: preferences?.environment || 'quiet space',
          voice_gender: preferences?.voiceGender || 'female',
          specific_voice_id: preferences?.specific_voice_id || null,
          meditation_style: preferences?.preferredStyle || category.name.toLowerCase(),
          voice_style: preferences?.voiceType || 'calm',
          seeking_goal: preferences?.primaryGoal || `${category.name.toLowerCase()} session`,
          session_length: preferences?.sessionLength || '5 minutes',
          desired_feeling: preferences?.desiredFeeling || 'peaceful and relaxed',

          // Optional fields
          words_to_avoid: preferences?.triggers || '',
          include_background_sounds: preferences?.backgroundSounds ?? true,
          background_music_file: preferences?.backgroundMusicSelection || null,

          // Your existing custom fields
          prompt: zenAFPrompt,
          groupSession: groupSessionScope.sessionScope === 'group' ? groupSessionScope.groupSession : undefined,
          usePersonalBiography: groupSessionScope.sessionScope === 'group' ? false : (preferences?.usePersonalBiography ?? true),
          allowedNames: groupSessionScope.sessionScope === 'group' ? { usage: 'neutral' } : ((preferences?.usePersonalBiography ?? true) ? {
            firstName: profile?.first_name || user?.user_metadata?.first_name,
            preferredName: profile?.preferred_name || user?.user_metadata?.preferred_name,
            usage: profile?.name_usage_preference || user?.user_metadata?.name_usage_preference || 'first'
          } : { usage: 'neutral' })
        }, {
          onProgress: (progress, message) => {
            console.log(`📊 Generation progress: ${Math.round(progress * 100)}% - ${message}`);
          },
          onJobCreated: (jobId) => {
            console.log('🔍 Job created, starting tracking:', jobId);
            queueStatus.startTracking(jobId);
          }
        });

        contentData = response.data;
        contentError = response.error;

        console.log('🔧 Meditation API response:', contentData);

        if (contentError) {
          console.error('❌ Content generation error:', contentError);
          throw contentError;
        }

        // generateMeditationAsync already handles polling internally and returns the script directly
        if (!contentData?.script) {
          console.error('❌ No script in response:', contentData);
          throw new Error('No content generated - please try again');
        }

        finalContent = contentData.script;
        console.log('✅ Script received, length:', finalContent.length);

      // Handle service unavailable mode (DLQ) - only for generated content
      if (contentData) {
        const mode = contentData.mode || 'audio';

        if (mode === 'service_unavailable') {
          console.warn('⚠️ Audio service unavailable - job queued for retry');
          toast({
            title: "Script Ready - Audio Pending",
            description: "Audio generation is queued and will retry automatically. Check session history for updates.",
            variant: "default"
          });
          // Don't save session or set audio URL - let DLQ worker handle it
          return;
        }
      }

      // Audio URL is now included in contentData from async API
      const audioUrl = contentData?.audio_url || contentData?.mixed_audio_url;

      if (!audioUrl) {
        throw new Error('Audio generation failed - please try again');
      }

      console.log('✅ Audio URL received:', audioUrl);

      // Check if backend worker already created the session (new architecture)
      let sessionId = contentData?.session_id;

      if (sessionId) {
        // Backend worker already created the session - use that ID
        console.log('✅ Using session created by backend worker:', sessionId);
        setGeneratedSessionId(sessionId);
      } else {
        // Backward compatibility: create session if backend didn't
        console.log('📝 Backend did not create session, creating in frontend (old flow)');
        const sessionDuration = preferences?.sessionLength ?
          (typeof preferences.sessionLength === 'string' ? parseInt(preferences.sessionLength.split(' ')[0] || '3') * 60 : preferences.sessionLength * 60) :
          180;
        const { data: session, error: saveError } = await backendClient
          .from('sessions')
          .insert({
            user_id: user.id,
            category_id: categoryId!,
            title: actualTitle,
            audio_url: audioUrl,
            session_type: actualSessionType,
            duration: sessionDuration,
            session_scope: groupSessionScope.sessionScope,
            group_session_id: groupSessionScope.groupSessionId || null,
            background_music_file: preferences?.backgroundMusicSelection || null
          })
          .select()
          .single();

        if (saveError) throw saveError;

        // Set the generated session ID
        if (session) {
          setGeneratedSessionId(session.id);
        }
      }

      setAudioUrl(audioUrl);
      setCurrentBackgroundMusicFile(preferences?.backgroundMusicSelection || undefined);
      queueStatus.stopTracking(); // Stop tracking when generation completes
      toast({
        title: "Session Generated",
        description: "Your meditation session is ready to play."
      });

    } catch (error: any) {
      console.error('Generation error:', error);
      queueStatus.stopTracking(); // Stop tracking on error too
      toast({
        title: "Generation Failed",
        description: error.message || "Failed to generate session. Please try again.",
        variant: "destructive",
        duration: 15000, // Stay visible for 15 seconds so user doesn't miss it
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Helper function for template-based generation
  const generateFromTemplate = async (templatePrompt: string, title: string, biography?: string, preferences?: QuestionnaireData) => {
    // Loading state managed by calling function
    
    try {
      const isZenAF = categoryId === 'zen-af' || categoryId === 'news-meditation';
      const zenAFPrompt = isZenAF ? `ZEN AF EXPLICIT THERAPEUTIC SESSION - ${templatePrompt}` : templatePrompt;
      
      // 2. In the generateFromTemplate function (around line 1130)
      const { data: contentData, error: contentError } = await generateMeditationAsync({
        // Required fields
        category_id: categoryId!,
        form_type: categoryId!,
        title: preferences?.sessionTitle || title || undefined, // User-specified or template title
        environment: preferences?.environment || 'quiet space',
        voice_gender: preferences?.voiceGender || 'female',
        specific_voice_id: preferences?.specific_voice_id || null,
        meditation_style: preferences?.preferredStyle || category.name.toLowerCase(),
        voice_style: preferences?.voiceType || 'calm',
        seeking_goal: preferences?.primaryGoal || `personalized ${category.name.toLowerCase()}`,
        session_length: preferences?.sessionLength || '5 minutes',
        desired_feeling: preferences?.desiredFeeling || 'centered and calm',

        // Optional fields
        words_to_avoid: preferences?.triggers || '',
        include_background_sounds: preferences?.backgroundSounds ?? true,
        background_music_file: preferences?.backgroundMusicSelection || null,

        // Extended questionnaire data - send ALL questionnaire fields
        questionnaire_data: preferences || {},

        // Personal information from questionnaire - only include if toggle is ON
        first_name: (preferences?.usePersonalBiography ?? true) ? (preferences?.firstName || profile?.first_name || user?.user_metadata?.first_name) : undefined,
        last_name: (preferences?.usePersonalBiography ?? true) ? (preferences?.lastName || profile?.last_name || user?.user_metadata?.last_name) : undefined,
        // Personal details only included if usePersonalBiography is ON
        age: (preferences?.usePersonalBiography ?? true) ? preferences?.age : undefined,
        occupation: (preferences?.usePersonalBiography ?? true) ? preferences?.occupation : undefined,
        relationship: (preferences?.usePersonalBiography ?? true) ? preferences?.relationship : undefined,
        location: (preferences?.usePersonalBiography ?? true) ? preferences?.location : undefined,
        hobbies: (preferences?.usePersonalBiography ?? true) ? preferences?.hobbies : undefined,
        stressors: (preferences?.usePersonalBiography ?? true) ? preferences?.stressors : undefined,
        goals: (preferences?.usePersonalBiography ?? true) ? preferences?.goals : undefined,
        biography: (preferences?.usePersonalBiography ?? true) ? (preferences?.biography || biography) : undefined,

        // Category-specific data
        category_specific_data: {
          experienceLevel: preferences?.experienceLevel,
          yogaStyle: preferences?.yogaStyle,
          physicalLimitations: preferences?.physicalLimitations,
          beginningPosition: preferences?.beginningPosition,
          endingPosition: preferences?.endingPosition,
          focusAreas: preferences?.focusAreas,
          breathWork: preferences?.breathWork,
          sessionIntention: preferences?.sessionIntention,
          sessionType: preferences?.sessionType,
          storyAudience: preferences?.storyAudience,
          storyTheme: preferences?.storyTheme,
          includePersonalDetails: preferences?.includePersonalDetails,
          massageType: preferences?.massageType,
          targetBodyParts: preferences?.targetBodyParts,
          currentState: preferences?.currentState,
          currentNews: preferences?.currentNews,
          newsConsumption: preferences?.newsConsumption,
          explicitLanguageLevel: preferences?.explicitLanguageLevel,
          humorLevel: preferences?.humorLevel,
          groupFrustration: preferences?.groupFrustration,
          deeperReason: preferences?.deeperReason,
          recoveryStage: preferences?.recoveryStage,
          primaryFocus: preferences?.primaryFocus,
          emotionalNeeds: preferences?.emotionalNeeds,
          decisionStage: preferences?.decisionStage,
          primaryConcern: preferences?.primaryConcern,
          valuesExploration: preferences?.valuesExploration,
          emotionalState: preferences?.emotionalState,
          supportNeeded: preferences?.supportNeeded,
          sessionApproach: preferences?.sessionApproach,
          healingApproach: preferences?.healingApproach,
          currentChallenges: preferences?.currentChallenges,
          emotionalTone: preferences?.emotionalTone,
          celebrationType: preferences?.celebrationType,
          celebrationFocus: preferences?.celebrationFocus,
          energyLevel: preferences?.energyLevel,
          specialPeople: preferences?.specialPeople,
        },

        // Custom fields
        prompt: zenAFPrompt,
        categoryId: categoryId,
        groupSession: groupSessionScope.sessionScope === 'group' ? groupSessionScope.groupSession : undefined,
        usePersonalBiography: groupSessionScope.sessionScope === 'individual' ? (preferences?.usePersonalBiography ?? true) : false,
        allowedNames: (categoryId === 'news-meditation') ? {
          usage: 'news-permissive',
          // Only include names if toggle is ON
          firstName: (preferences?.usePersonalBiography ?? true) ? (profile?.first_name || user?.user_metadata?.first_name) : undefined,
          preferredName: (preferences?.usePersonalBiography ?? true) ? (profile?.preferred_name || user?.user_metadata?.preferred_name) : undefined,
        } : (
          (groupSessionScope.sessionScope === 'individual' && (preferences?.usePersonalBiography ?? true)) ? {
            firstName: profile?.first_name || user?.user_metadata?.first_name,
            preferredName: profile?.preferred_name || user?.user_metadata?.preferred_name,
            usage: profile?.name_usage_preference || user?.user_metadata?.name_usage_preference || 'first'
          } : { usage: 'neutral' }
        )
      }, {
        onProgress: (progress, message) => {
          console.log(`📊 Template generation progress: ${Math.round(progress * 100)}% - ${message}`);
        },
        onJobCreated: (jobId) => {
          console.log('🔍 Job created for template, starting tracking:', jobId);
          queueStatus.startTracking(jobId);
        }
      });

      if (contentError) throw contentError;
      if (!contentData?.script) throw new Error('No content generated from template');

      // Audio URL is now included in contentData from async API
      const audioUrl = contentData.audio_url || contentData.mixed_audio_url;
      const mode = contentData.mode || 'audio';

      // Handle service unavailable mode (DLQ)
      if (mode === 'service_unavailable') {
        console.warn('⚠️ Audio service unavailable - job queued for retry');
        toast({
          title: "Script Ready - Audio Pending",
          description: "Audio generation is queued and will retry automatically. Check session history for updates.",
          variant: "default"
        });
        return;
      }

      if (!audioUrl) throw new Error('No audio generated from template');

      setAudioUrl(audioUrl);
      if (groupSessionScope.sessionScope === 'group') {
        console.log('🛟 Using fallback player for group session (iframe safety)');
        setUseAudioFallback(true);
      }

      console.log('🎵 Template audio URL generated successfully:', audioUrl);

      // Force a state refresh to ensure audio player picks up the new URL
      setTimeout(() => {
        console.log('🔍 Template post-generation state check:', {
          audioUrl: !!audioUrl,
          sessionTitle: title,
          audioUrlLength: audioUrl?.length || 0
        });
      }, 100);

      // Check if backend worker already created the session (new architecture)
      let sessionId = contentData?.session_id;

      if (sessionId) {
        // Backend worker already created the session - use that ID
        console.log('✅ Using session created by backend worker:', sessionId);
        setGeneratedSessionId(sessionId);
      } else {
        // Backward compatibility: create session if backend didn't
        console.log('📝 Backend did not create session, creating in frontend (old flow)');
        const sessionDuration = preferences?.sessionLength ?
          (typeof preferences.sessionLength === 'string' ? parseInt(preferences.sessionLength.split(' ')[0] || '3') * 60 : preferences.sessionLength * 60) :
          (category?.personalizedTemplateDuration || 180);
        const { data: session, error } = await backendClient.from('sessions').insert({
          user_id: user.id,
          category_id: categoryId!,
          title: title,
          audio_url: audioUrl,
          session_type: 'template-personalized',
          duration: sessionDuration,
          session_scope: groupSessionScope.sessionScope,
          group_session_id: groupSessionScope.groupSessionId || null,
          background_music_file: getBackgroundMusicSelection()
        }).select().single();

        if (!error && session) {
          setGeneratedSessionId(session.id);
        }
      }
      setCurrentBackgroundMusicFile(getBackgroundMusicSelection());
      queueStatus.stopTracking(); // Stop tracking when generation completes

      toast({
        title: "Template Session Generated",
        description: "Your personalized session is ready using our optimized template",
      });

    } catch (error: any) {
      console.error('Template generation error:', error);
      queueStatus.stopTracking(); // Stop tracking on error
      toast({
        title: "Template Generation Failed",
        description: "Falling back to full generation...",
        variant: "destructive"
      });

      // Fallback to full generation
      generateAudio(category?.samplePrompt || '', title, biography, preferences);
    } finally {
      // Loading state managed by calling function
    }
  };

  const getCategorySpecificText = () => {
    const categoryTexts: Record<string, string> = {
      meditation: 'Personalized Meditation',
      anxiety: 'Personalized Relief',
      sleep: 'Personalized Sleep Journey',
      massage: 'Personalized Body Work',
      motivation: 'Personalized Motivation',
      memory: 'Personalized Brain Training',
      be_still: 'Personalized Emotional Support',
      self_love: 'Personalized Self-Love'
    };

    return categoryTexts[categoryId || ''] || 'Personalized Session';
  };

  const categoryText = getCategorySpecificText();


  const handleStartPersonalization = () => {
    console.log("🔥 handleStartPersonalization called, categoryId:", categoryId);
    console.log("🔥 Current showPersonalization state:", showPersonalization);

    if (!categoryId) {
      console.log("🔥 ERROR: No categoryId found");
      return;
    }

    // For individual sessions, always show biography step first
    if (selectedSessionType === 'individual') {
      console.log("🔥 Showing biography step for individual session");
      setShowBiographyStep(true);
      return;
    }

    // For group sessions, proceed directly to questionnaire
    console.log("🔥 Setting showPersonalization to true");
    setShowPersonalization(true);
  };

  // Biography step handlers
  const handleBiographyStepContinue = async () => {
    // Save biography to profile if there's content
    if (biography.trim() && user) {
      try {
        await backendClient
          .from('profiles')
          .update({ biography })
          .eq('id', user.id);

        toast({
          title: "Biography Saved",
          description: "Your profile has been updated."
        });
      } catch (error: any) {
        console.error('Error saving biography:', error);
        // Don't block progression on save error
      }
    }

    // Close biography step and open questionnaire
    setShowBiographyStep(false);
    setShowPersonalization(true);
  };

  const handleBiographyStepSkip = () => {
    // Close biography step and open questionnaire without saving
    setShowBiographyStep(false);
    setShowPersonalization(true);
  };

  const handleBiographyStepCancel = () => {
    // Just close the biography step
    setShowBiographyStep(false);
  };

  const handleSessionTypeSelection = (type: 'individual' | 'group') => {
    console.log("🎯 Session type selected:", type);
    
    try {
      // Update groupSessionScope based on selection
      if (type === 'individual') {
        setGroupSessionScope({ sessionScope: 'individual' });
      } else {
        // For group sessions, preserve existing group data or reset to empty group state
        setGroupSessionScope(prev => ({ 
          sessionScope: 'group',
          groupSessionId: prev.groupSessionId,
          groupSession: prev.groupSession
        }));
      }
      
      // Set the selected type and mark as selected
      setSelectedSessionType(type);
      setSessionTypeSelected(true);
      
      toast({
        title: "Session Type Selected",
        description: `${type === 'individual' ? 'Individual' : 'Group'} session ready to configure.`,
        duration: 2000,
      });
      
    } catch (error) {
      console.error("❌ Error in session type selection:", error);
      toast({
        title: "Selection Error",
        description: "Please try selecting your session type again.",
        variant: "destructive",
        duration: 10000,
      });
    }
  };

  const handleBackToTypeSelection = () => {
    setSessionTypeSelected(false);
    setAudioUrl(null);
    setSessionTitle('');
  };

  const handlePersonalizationSubmit = async (questionnaireData: QuestionnaireData) => {
    console.log('🔥 handlePersonalizationSubmit called with data:', questionnaireData);
    
    if (!user) {
      console.log('🚨 No user found, showing auth error');
      toast({
        title: "Authentication Required",
        description: "Please sign in to use personalized sessions.",
        variant: "destructive",
        duration: 10000,
      });
      setIsGenerating(false);
      return;
    }

    console.log('🔥 User authenticated, starting generation process');

    // Check credits before generation
    const sessionLengthMinutes = questionnaireData.sessionLength
      ? parseInt(questionnaireData.sessionLength.split(' ')[0])
      : 5; // Default to 5 minutes
    const estimatedCredits = Math.ceil(sessionLengthMinutes * audioRate);

    // Block if insufficient credits
    if (!checkBeforeAction(estimatedCredits)) {
      setIsGenerating(false);
      return;
    }

    // Warn if will be low after this action
    warnBeforeAction(estimatedCredits);

    // Show cost estimate to user
    toast({
      title: "Generating Session",
      description: `Using ~${estimatedCredits} credits for this ${sessionLengthMinutes}-minute session`,
      duration: 3000,
    });

    console.log('🔥 Setting isGenerating to true');
    setIsGenerating(true);

    // Save user preferences for future prefilling
    if (questionnaireData.backgroundMusicSelection) {
      console.log('🎵 Saving background music preference:', questionnaireData.backgroundMusicSelection);
      updatePreference({
        category: 'background_music',
        preferenceType: 'background_music_selection',
        value: questionnaireData.backgroundMusicSelection,
        confidenceBoost: 0.3
      });
    }

    // Save voice gender preference
    if (questionnaireData.voiceGender) {
      console.log('🎤 Saving voice gender preference:', questionnaireData.voiceGender);
      updatePreference({
        category: 'voice',
        preferenceType: 'voice_gender',
        value: questionnaireData.voiceGender,
        confidenceBoost: 0.3
      });
    }

    // Save voice type preference
    if (questionnaireData.voiceType) {
      console.log('🎙️ Saving voice type preference:', questionnaireData.voiceType);
      updatePreference({
        category: 'voice',
        preferenceType: 'voice_type',
        value: questionnaireData.voiceType,
        confidenceBoost: 0.3
      });
    }

    // Save session length preference
    if (questionnaireData.sessionLength) {
      console.log('⏱️ Saving session length preference:', questionnaireData.sessionLength);
      const sessionSeconds = parseInt(questionnaireData.sessionLength.split(' ')[0]) * 60;
      updatePreference({
        category: 'session',
        preferenceType: 'session_length',
        value: String(sessionSeconds),
        confidenceBoost: 0.3
      });
    }

    // Save personal information to profile for future prefilling
    try {
      const isGroupMode = groupSessionScope.sessionScope === 'group';

      // Only save personal info for individual sessions
      if (!isGroupMode) {
        const sessionLengthSeconds = questionnaireData.sessionLength
          ? parseInt(questionnaireData.sessionLength.split(' ')[0]) * 60
          : 300;

        // Helper to extract category-specific fields
        const extractCategoryFields = () => {
          const categoryFields: Record<string, any> = {};

          const fieldMappings: Record<string, string[]> = {
            'yoga-meditation': ['experienceLevel', 'yogaStyle', 'physicalLimitations', 'beginningPosition', 'endingPosition', 'focusAreas', 'breathWork', 'sessionIntention'],
            'zen-af': ['explicitLanguageLevel', 'humorLevel', 'specificFocus', 'deeperReason'],
            'news-meditation': ['currentNews', 'newsConsumption', 'explicitLanguageLevel', 'humorLevel'],
            'addiction-recovery-men': ['recoveryStage', 'primaryFocus', 'emotionalNeeds'],
            'relationship-decisions': ['decisionStage', 'primaryConcern', 'valuesExploration', 'emotionalState', 'supportNeeded', 'sessionApproach'],
            'sleep': ['sessionType', 'storyAudience', 'storyTheme'],
            'massage': ['massageType', 'targetBodyParts'],
            'couples-connection': ['relationshipStage', 'primaryGoal', 'communicationStyle'],
            'sexual-wellness-women': ['primaryFocus', 'healingApproach', 'emotionalTone']
          };

          const relevantFields = fieldMappings[categoryId || ''] || [];
          relevantFields.forEach(field => {
            const value = (questionnaireData as any)[field];
            if (value !== undefined && value !== null && value !== '') {
              categoryFields[field] = value;
            }
          });

          return categoryFields;
        };

        const personalInfoUpdate = {
          age: questionnaireData.age,
          occupation: questionnaireData.occupation,
          relationship_status: questionnaireData.relationship,
          location: questionnaireData.location,
          hobbies: questionnaireData.hobbies,
          stressors: questionnaireData.stressors,
          goals: questionnaireData.goals,
          default_voice_gender: questionnaireData.voiceGender,
          default_voice_type: questionnaireData.voiceType,
          default_session_length: sessionLengthSeconds,
          default_background_music: questionnaireData.backgroundMusicSelection,
          default_environment: questionnaireData.environment,
          // Merge category-specific preferences
          category_preferences: {
            ...(profile?.category_preferences || {}),
            [categoryId || 'default']: {
              preferredStyle: questionnaireData.preferredStyle,
              primaryGoal: questionnaireData.primaryGoal,
              triggers: questionnaireData.triggers,
              desiredFeeling: questionnaireData.desiredFeeling,
              ...extractCategoryFields()
            }
          }
        };

        // Remove undefined values
        const cleanedUpdate = Object.fromEntries(
          Object.entries(personalInfoUpdate).filter(([_, value]) =>
            value !== undefined && value !== null && value !== ''
          )
        );

        console.log('💾 Saving personal information to profile:', cleanedUpdate);

        // Save to database
        const { error: profileError } = await backendClient
          .from('profiles')
          .update(cleanedUpdate)
          .eq('id', user.id);

        if (profileError) {
          console.error('❌ Error saving personal information:', profileError);
          // Don't block session generation if save fails
          toast({
            title: "Note",
            description: "Personal information could not be saved, but session will continue.",
            variant: "default"
          });
        } else {
          console.log('✅ Personal information saved successfully');
        }
      }
    } catch (error) {
      console.error('❌ Error updating profile with personal info:', error);
      // Don't block session generation on error
    }

    // Use the rich questionnaire data directly
    const enhancedData: QuestionnaireData = {
      ...questionnaireData,
      sessionLength: questionnaireData.sessionLength || '5 minutes'
    };

    // Parse session length
    const sessionMinutes = parseInt(enhancedData.sessionLength.split(' ')[0]) || 5;
    
    try {
      console.log('Starting advanced personalized session generation...');

      // Determine if this is group mode or individual mode
      const isGroupMode = groupSessionScope.sessionScope === 'group';
      
      // For group mode: exclude ALL personal biographical data
      // For individual mode: include personal data only if usePersonalBiography is true
      const shouldUsePersonalData = !isGroupMode && (questionnaireData?.usePersonalBiography ?? true);
      
      const profileData = shouldUsePersonalData ? {
        first_name: profile?.first_name,
        preferred_name: profile?.preferred_name,
        name_usage_preference: profile?.name_usage_preference
      } : undefined;

      // Filter out personal biographical data for group mode OR when toggle is OFF
      const shouldFilterPersonalData = isGroupMode || !shouldUsePersonalData;
      const filteredData = shouldFilterPersonalData ? {
        ...enhancedData,
        firstName: undefined,
        lastName: undefined,
        name: undefined,
        age: undefined,
        occupation: undefined,
        relationship: undefined,
        location: undefined,
        hobbies: undefined,
        stressors: undefined,
        goals: undefined,
        biography: undefined,  // CRITICAL: Filter biography to prevent name leaks from bio text
        usePersonalBiography: false
      } : enhancedData;

      // Create sophisticated prompt using enhanced prompt generation with full questionnaire utilization
      const rawEnhancedPrompt = createEnhancedCategoryPrompt(
        category.name,
        filteredData,
        // Never pass personal biography to group sessions
        isGroupMode ? undefined : (shouldUsePersonalData ? (questionnaireData.biography || biography) : undefined),
        profileData,
        isGroupMode ? undefined : (shouldUsePersonalData ? user : undefined),
        isGroupMode ? groupSessionScope.groupSession : undefined
      );

      // Process session length placeholders to replace {{SESSION_LENGTH}} and {{TARGET_WORDS}}
      const sessionLengthStr = filteredData.sessionLength || '5 minutes';
      const enhancedPrompt = processSessionLengthPlaceholders(rawEnhancedPrompt, sessionLengthStr);

      console.log('Enhanced prompt created for category:', category.name);
      console.log('Session length:', sessionLengthStr, '- Placeholders processed');

      // Pre-flight: log personalization data and guarantee extra context inclusion
      const extraFocus = (filteredData as any).additionalInfo || (filteredData as any).additionalNotes;
      console.log('🧾 PersonalizationDataUsed', {
        specificFocus: (filteredData as any).specificFocus,
        desiredFeeling: filteredData.desiredFeeling,
        triggers: filteredData.triggers,
        primaryGoal: filteredData.primaryGoal,
        environment: filteredData.environment,
        useEnvironment: filteredData.useEnvironment,
        backgroundSounds: filteredData.backgroundSounds,
        backgroundMusicSelection: filteredData.backgroundMusicSelection,
        extraFocus
      });

      // ZEN-AF EXPLICIT LANGUAGE LEVEL TRACKING
      console.log('🔥 ZEN-AF EXPLICIT LANGUAGE TRACKING:', {
        categoryId: categoryId,
        isZenAF: categoryId === 'zen-af' || categoryId === 'news-meditation',
        explicitLanguageLevel: filteredData.explicitLanguageLevel,
        rawFormData_explicitLevel: (questionnaireData as any)?.explicitLanguageLevel,
        filteredData_keys: Object.keys(filteredData),
        hasExplicitInFiltered: 'explicitLanguageLevel' in filteredData
      });

      // Additional logging for zen-af specific fields
      if (categoryId === 'zen-af') {
        console.log('🎯 ZEN-AF SPECIFIC FIELDS:', {
          specificFocus: filteredData.specificFocus,
          deeperReason: filteredData.deeperReason,
          groupFrustration: filteredData.groupFrustration,
          sessionType: groupSessionScope.sessionScope,
          isGroupSession: groupSessionScope.sessionScope === 'group',
          explicitLanguageLevel: filteredData.explicitLanguageLevel || 8
        });
      }

      let preflightPrompt = enhancedPrompt;
      if (extraFocus && !enhancedPrompt.includes(extraFocus)) {
        preflightPrompt = `${enhancedPrompt}\n\nIMPORTANT ADDITIONAL CONTEXT: ${extraFocus}`;
      }

      // Use the advanced audio generation system  
      try {
        // Generate content first - ensure ZEN AF uses proper function with explicit detection
        const isZenAF = categoryId === 'zen-af' || categoryId === 'news-meditation';
        const zenAFPrompt = isZenAF ? `ZEN AF EXPLICIT THERAPEUTIC SESSION - ${preflightPrompt}` : preflightPrompt;
        
        console.log('📋 Attempting to generate meditation content...');
        console.log('🔍 Edge function call details:', {
          functionName: 'generate-meditation',
          promptLength: zenAFPrompt.length,
          hasGroupSession: !!(groupSessionScope.sessionScope === 'group' ? groupSessionScope.groupSession : undefined),
          usePersonalBiography: groupSessionScope.sessionScope === 'individual' ? (questionnaireData?.usePersonalBiography ?? true) : false
        });

        // Log category_specific_data being sent to backend
        const categorySpecificDataToSend = (() => {
          let categoryData: any = {};

          // Build the same structure as we're sending to backend
          if (categoryId === 'zen-af') {
            categoryData = {
              specificFocus: filteredData.specificFocus,
              deeperReason: filteredData.deeperReason,
              groupFrustration: filteredData.groupFrustration,
              explicitLanguageLevel: filteredData.explicitLanguageLevel || 8,
              humorLevel: filteredData.humorLevel
            };
          } else if (categoryId === 'news-meditation') {
            categoryData = {
              currentNews: filteredData.currentNews,
              newsConsumption: filteredData.newsConsumption,
              explicitLanguageLevel: filteredData.explicitLanguageLevel || 5,
              humorLevel: filteredData.humorLevel || 'Moderate'
            };
          }
          // Add other categories as needed for debugging

          // Remove undefined values
          return Object.fromEntries(
            Object.entries(categoryData).filter(([_, value]) => value !== undefined && value !== null && value !== '')
          );
        })();

        console.log('🔥 CATEGORY_SPECIFIC_DATA BEING SENT TO BACKEND:', categorySpecificDataToSend);
        console.log('🔥 Category:', categoryId, '| Fields included:', Object.keys(categorySpecificDataToSend));
        console.log('🔥 EXPLICIT LANGUAGE LEVEL VALUE:', filteredData.explicitLanguageLevel, 'Type:', typeof filteredData.explicitLanguageLevel);

        // 3. In handlePersonalizationSubmit (around line 1885)
        const { data: contentData, error: contentError } = await generateMeditationAsync({
          // Required fields from questionnaire data
          category_id: categoryId!,
          form_type: categoryId!,
          title: filteredData.sessionTitle || undefined, // User-specified session title
          environment: filteredData.environment || 'quiet space',
          voice_gender: enhancedData.voiceGender || 'female',
          specific_voice_id: enhancedData.specific_voice_id || null,
          meditation_style: enhancedData.preferredStyle || category.name.toLowerCase(),
          voice_style: enhancedData.voiceType || 'calm',
          seeking_goal: enhancedData.primaryGoal || 'inner peace',
          session_length: String(sessionMinutes * 60),
          desired_feeling: filteredData.desiredFeeling || 'peaceful',

          // Optional fields
          words_to_avoid: filteredData.triggers || '',
          include_background_sounds: filteredData.backgroundSounds ?? true,
          background_music_file: enhancedData?.backgroundMusicSelection || null,

          // Extended questionnaire data - send ALL questionnaire fields
          questionnaire_data: filteredData || {},

          // Personal information from questionnaire - only include if toggle is ON
          first_name: shouldUsePersonalData ? (filteredData.firstName || profile?.first_name || user?.user_metadata?.first_name) : undefined,
          last_name: shouldUsePersonalData ? (filteredData.lastName || profile?.last_name || user?.user_metadata?.last_name) : undefined,
          age: filteredData.age,
          occupation: filteredData.occupation,
          relationship: filteredData.relationship,
          location: filteredData.location,
          hobbies: filteredData.hobbies,
          stressors: filteredData.stressors,
          goals: filteredData.goals,
          biography: filteredData.biography || (shouldUsePersonalData ? biography : undefined),

          // Category-specific data - only include fields relevant to current category
          category_specific_data: (() => {
            let categoryData: any = {};

            // Build category-specific object based on categoryId
            if (categoryId === 'zen-af') {
              categoryData = {
                specificFocus: filteredData.specificFocus,  // Individual sessions
                deeperReason: filteredData.deeperReason,
                groupFrustration: filteredData.groupFrustration,  // Group sessions
                explicitLanguageLevel: filteredData.explicitLanguageLevel || 8,  // Default to 8
                humorLevel: filteredData.humorLevel
              };
            } else if (categoryId === 'yoga-meditation') {
              categoryData = {
                experienceLevel: filteredData.experienceLevel,
                yogaStyle: filteredData.yogaStyle,
                physicalLimitations: filteredData.physicalLimitations,
                beginningPosition: filteredData.beginningPosition,
                endingPosition: filteredData.endingPosition,
                focusAreas: filteredData.focusAreas,
                breathWork: filteredData.breathWork,
                sessionIntention: filteredData.sessionIntention
              };
            } else if (categoryId === 'sleep') {
              categoryData = {
                sessionType: filteredData.sessionType,
                storyAudience: filteredData.storyAudience,
                storyTheme: filteredData.storyTheme,
                includePersonalDetails: filteredData.includePersonalDetails
              };
            } else if (categoryId === 'massage') {
              categoryData = {
                massageType: filteredData.massageType,
                targetBodyParts: filteredData.targetBodyParts,
                currentState: filteredData.currentState
              };
            } else if (categoryId === 'news-meditation') {
              categoryData = {
                currentNews: filteredData.currentNews,
                newsConsumption: filteredData.newsConsumption,
                explicitLanguageLevel: filteredData.explicitLanguageLevel || 5,  // Default for news
                humorLevel: filteredData.humorLevel || 'Moderate'
              };
            } else if (categoryId === 'addiction-recovery') {
              categoryData = {
                recoveryStage: filteredData.recoveryStage,
                primaryFocus: filteredData.primaryFocus,
                emotionalNeeds: filteredData.emotionalNeeds
              };
            } else if (categoryId === 'relationship-decisions') {
              categoryData = {
                decisionStage: filteredData.decisionStage,
                primaryConcern: filteredData.primaryConcern,
                valuesExploration: filteredData.valuesExploration,
                emotionalState: filteredData.emotionalState,
                supportNeeded: filteredData.supportNeeded,
                sessionApproach: filteredData.sessionApproach
              };
            } else if (categoryId === 'sexual-wellness') {
              categoryData = {
                healingApproach: filteredData.healingApproach,
                currentChallenges: filteredData.currentChallenges,
                emotionalTone: filteredData.emotionalTone
              };
            } else if (categoryId === 'life-celebrations') {
              categoryData = {
                celebrationType: filteredData.celebrationType,
                celebrationFocus: filteredData.celebrationFocus,
                energyLevel: filteredData.energyLevel,
                specialPeople: filteredData.specialPeople
              };
            } else if (categoryId === 'fitness') {
              categoryData = {
                workoutType: filteredData.workoutType,
                fitnessLevel: filteredData.fitnessLevel,
                motivationStyle: filteredData.motivationStyle,
                bodyFocus: filteredData.bodyFocus,
                mentalChallenges: filteredData.mentalChallenges,
                sessionTiming: filteredData.sessionTiming,
                specificFocus: filteredData.specificFocus,
                specialOccasions: filteredData.specialOccasions
              };
            }

            // Remove undefined values to keep payload clean
            return Object.fromEntries(
              Object.entries(categoryData).filter(([_, value]) => value !== undefined && value !== null && value !== '')
            );
          })(),

          // Custom fields
          prompt: zenAFPrompt,
          categoryId: categoryId,
          groupSession: groupSessionScope.sessionScope === 'group' ? groupSessionScope.groupSession : undefined,
          usePersonalBiography: groupSessionScope.sessionScope === 'individual' ? (questionnaireData?.usePersonalBiography ?? true) : false,
          allowedNames: (categoryId === 'news-meditation') ? {
            usage: 'news-permissive',
            // Only include names if toggle is ON
            firstName: (questionnaireData?.usePersonalBiography ?? true) ? (profile?.first_name || user?.user_metadata?.first_name) : undefined,
            preferredName: (questionnaireData?.usePersonalBiography ?? true) ? (profile?.preferred_name || user?.user_metadata?.preferred_name) : undefined,
          } : (
            (groupSessionScope.sessionScope === 'individual' && (questionnaireData?.usePersonalBiography ?? true)) ? {
              firstName: profile?.first_name || user?.user_metadata?.first_name,
              preferredName: profile?.preferred_name || user?.user_metadata?.preferred_name,
              usage: profile?.name_usage_preference || user?.user_metadata?.name_usage_preference || 'first'
            } : { usage: 'neutral' }
          )
        }, {
          onProgress: (progress, message) => {
            console.log(`📊 Questionnaire generation progress: ${Math.round(progress * 100)}% - ${message}`);
          },
          onJobCreated: (jobId) => {
            console.log('🔍 Job created for questionnaire, starting tracking:', jobId);
            queueStatus.startTracking(jobId);
          }
        });

        if (contentError) throw contentError;
        
        // Validate content was generated
        const generatedText = contentData?.script;
        if (!generatedText || typeof generatedText !== 'string' || generatedText.trim().length === 0) {
          throw new Error('No content was generated. Please try again.');
        }
        
        console.log('✅ Content generated successfully, length:', generatedText.length);

        // Validate ZEN AF content for originality and banned phrases (for zen-af category only)
        let finalContent = generatedText;
        let wasContentRegenerated = false;
        
        if (categoryId === 'zen-af') {
          console.log('🔍 Validating ZEN AF content for originality...');
          const validationResult = await validateAndRegenerateIfNeeded(
            generatedText, 
            '', // audioUrl placeholder
            zenAFPrompt
          );
          
          finalContent = validationResult.content;
          wasContentRegenerated = validationResult.wasRegenerated;
          
          if (wasContentRegenerated) {
            console.log('✅ Content was successfully regenerated for better originality');
          }
        }

        // Audio URL is now included in contentData from async API
        const generatedAudioUrl = contentData.audio_url || contentData.mixed_audio_url;
        const mode = contentData.mode || 'audio';

        // Handle service unavailable mode (DLQ)
        if (mode === 'service_unavailable') {
          console.warn('⚠️ Audio service unavailable - job queued for retry');
          toast({
            title: "Script Ready - Audio Pending",
            description: "Audio generation is queued and will retry automatically. Check session history for updates.",
            variant: "default"
          });
          return;
        }

        if (!generatedAudioUrl) {
          throw new Error('Audio generation failed - please try again');
        }

        console.log('✅ Audio URL received:', generatedAudioUrl);

        setAudioUrl(generatedAudioUrl);
        setSessionTitle(`Personalized ${category.name}`);
        setCurrentSessionType('personalized');

        if (groupSessionScope.sessionScope === 'group') {
          console.log('🛟 Using fallback player for group session (iframe safety)');
          setUseAudioFallback(true);
        }

        console.log('🎵 Audio URL generated successfully:', generatedAudioUrl);
        console.log('📝 Session title set:', `Personalized ${category.name}`);
        console.log('🎯 Audio generation pipeline completed successfully!');

        // Use session_id from worker response (worker already created the session)
        if (contentData.session_id) {
          console.log('✅ Using session ID from worker:', contentData.session_id);
          setGeneratedSessionId(contentData.session_id);
        } else {
          console.warn('⚠️ No session_id returned from worker');
        }

        setCurrentBackgroundMusicFile(enhancedData?.backgroundMusicSelection);
        queueStatus.stopTracking(); // Stop tracking when generation completes

        toast({
          title: "Advanced Personalized Session Generated",
          description: `Your sophisticated ${sessionMinutes}-minute session uses deep personalization with biography integration.`
        });

      } catch (generationError: any) {
        console.error('🚨 Content/Audio generation error details:', {
          message: generationError.message,
          code: generationError.code,
          status: generationError.status,
          stack: generationError.stack
        });
        
        // Provide more specific error messages
        if (generationError.message?.includes('non-2xx status code')) {
          throw new Error('Server is temporarily unavailable. Please try again in a moment.');
        } else if (generationError.message?.includes('Failed to fetch')) {
          throw new Error('Connection error. Please check your internet and try again.');
        }
        
        throw generationError; // Let the outer catch handle it
      }

    } catch (error: any) {
      console.error('🚨 Advanced personalization error details:', {
        message: error.message,
        type: error.name,
        cause: error.cause
      });
      
      // Clear any partial state before fallback
      setAudioUrl(null);
      setSessionTitle('');
      setCurrentSessionType(null);
      
      // Fallback to basic personalization if advanced fails
      console.log('⚡ Falling back to basic personalization...');
      // IMPORTANT: Only use biography if toggle is ON
      const shouldUseBio = questionnaireData?.usePersonalBiography ?? true;
      const biographyForFallback = shouldUseBio ? (biography || '') : '';
      const variables = extractUserVariables(biographyForFallback);
      const fallbackPrompt = `Create a personalized ${category.name.toLowerCase()} session with these preferences:

PERSONALIZATION PREFERENCES:
- Voice Gender: ${enhancedData.voiceGender}
- Session Length: ${sessionMinutes} minutes
- Voice Type: ${enhancedData.voiceType}
- Primary Goal: ${enhancedData.primaryGoal}
- Preferred Style: ${enhancedData.preferredStyle}

${biographyForFallback ? `Background context: ${biographyForFallback}` : ''}

Please create a session that incorporates their preferences and goals.

${category.samplePrompt || ''}`;

      // Basic generation with fallback
      const isZenAF = categoryId === 'zen-af' || categoryId === 'news-meditation';
      const zenAFFallbackPrompt = isZenAF ? `ZEN AF EXPLICIT THERAPEUTIC SESSION - ${fallbackPrompt}` : fallbackPrompt;
      
      const { data: contentData, error: contentError } = await generateMeditationAsync({
        // Required fields with fallback defaults
        category_id: categoryId!,
        form_type: categoryId!,
        title: enhancedData.sessionTitle || undefined, // User-specified session title
        environment: enhancedData.environment || 'peaceful setting',
        voice_gender: enhancedData.voiceGender || 'female',
        meditation_style: enhancedData.preferredStyle || category.name.toLowerCase(),
        voice_style: enhancedData.voiceType || 'soothing',
        seeking_goal: enhancedData.primaryGoal || 'relaxation',
        session_length: String(sessionMinutes * 60),
        desired_feeling: enhancedData.desiredFeeling || 'calm',

        // Optional fields
        words_to_avoid: '',
        include_background_sounds: true,
        background_music_file: enhancedData?.backgroundMusicSelection || null,

        // Custom fields
        prompt: zenAFFallbackPrompt,
        categoryId: categoryId,
        groupSession: groupSessionScope.sessionScope === 'group' ? groupSessionScope.groupSession : undefined,
        usePersonalBiography: false,
        allowedNames: (categoryId === 'news-meditation') ? {
          usage: 'news-permissive',
          // No names in fallback mode (usePersonalBiography is false)
          firstName: undefined,
          preferredName: undefined,
        } : { usage: 'neutral' }
      }, {
        onProgress: (progress, message) => {
          console.log(`📊 Fallback generation progress: ${Math.round(progress * 100)}% - ${message}`);
        },
        onJobCreated: (jobId) => {
          console.log('🔍 Job created for fallback, starting tracking:', jobId);
          queueStatus.startTracking(jobId);
        }
      });

      if (contentError) throw contentError;

      // Validate content was generated
      if (!contentData?.script || typeof contentData.script !== 'string' || contentData.script.trim().length === 0) {
        throw new Error('No script was generated in fallback. Please try again.');
      }

      // Audio URL is now included in contentData from async API
      const fallbackAudioUrl = contentData.audio_url || contentData.mixed_audio_url;
      const mode = contentData.mode || 'audio';

      // Handle service unavailable mode (DLQ)
      if (mode === 'service_unavailable') {
        console.warn('⚠️ Audio service unavailable - job queued for retry');
        toast({
          title: "Script Ready - Audio Pending",
          description: "Audio generation is queued and will retry automatically. Check session history for updates.",
          variant: "default"
        });
        return;
      }

      if (!fallbackAudioUrl) {
        toast({
          title: "Partial Success",
          description: "Content generated but audio failed. Please try again.",
          variant: "destructive",
          duration: 15000,
        });
        return;
      }

      console.log('✅ Fallback audio URL received:', fallbackAudioUrl);

      // Check if backend worker already created the session (new architecture)
      let sessionId = contentData?.session_id;

      if (sessionId) {
        // Backend worker already created the session - use that ID
        console.log('✅ Using session created by backend worker:', sessionId);
        setGeneratedSessionId(sessionId);
      } else {
        // Backward compatibility: create session if backend didn't
        console.log('📝 Backend did not create session, creating in frontend (old flow)');
        const { data: session, error: insertError } = await backendClient.from('sessions').insert({
          user_id: user.id,
          category_id: categoryId!,
          title: `Personalized ${category.name}`,
          audio_url: fallbackAudioUrl,
          session_type: 'personalized',
          duration: sessionMinutes * 60,
          session_scope: groupSessionScope.sessionScope,
          group_session_id: groupSessionScope.groupSessionId || null,
          background_music_file: enhancedData?.backgroundMusicSelection || null
        }).select().single();

        if (!insertError && session) {
          setGeneratedSessionId(session.id);
        }
      }
      setCurrentBackgroundMusicFile(enhancedData?.backgroundMusicSelection);

      setAudioUrl(fallbackAudioUrl);
      setSessionTitle(`Personalized ${category.name}`);
      setCurrentSessionType('personalized');
      queueStatus.stopTracking(); // Stop tracking when generation completes

      toast({
        title: "Fallback Session Generated",
        description: `Your personalized ${sessionMinutes}-minute session is ready.`
      });

    } finally {
      setIsGenerating(false);
      setShowPersonalization(false);
    }
  };

  if (!category) return null;

  // Show session type selector if not selected yet
  if (!sessionTypeSelected) {
    return (
      <RoleBasedCategoryAccess categoryId={categoryId!}>
        <div className="min-h-screen">
          <div className="container mx-auto px-6 py-8 max-w-6xl">
            {/* Header */}
            <div className="flex items-center justify-between gap-4 mb-6">
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  console.log("🔥 Back to Categories button clicked");
                  console.log("🔥 Event:", e);
                  console.log("🔥 navigate function:", navigate);
                  e.stopPropagation();
                  navigate('/app');
                  console.log("🔥 navigate('/app') called");
                }}
                className="rounded-full font-medium text-sm hover:scale-[1.02] transition-all duration-300"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Button>

              {/* Credit Balance Display */}
              {credits && (
                <div className="flex items-center gap-4 px-4 py-2 rounded-full bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20">
                  <div className="flex items-center gap-2">
                    <Coins className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">
                      Available: <span className="text-primary font-bold">{totalAvailableCredits}</span> credits
                    </span>
                  </div>
                  <div className="h-4 w-px bg-border"></div>
                  <span className="text-xs text-muted-foreground">
                    ~{audioRate} credits/min
                  </span>
                </div>
              )}
            </div>

            <div className="text-center mb-12 space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-primary/20 to-accent/20 border border-primary/30 mb-4">
                <Sparkles className="h-4 w-4 text-primary animate-pulse" />
                <span className="text-sm font-medium text-primary">Choose Your Experience</span>
              </div>
              
              <div className="text-5xl opacity-90 mb-6">
                {React.createElement(category.icon, { className: `h-20 w-20 mx-auto ${getCategoryIconColor(categoryId || '')} filter drop-shadow-lg` })}
              </div>
              
              <h1 className="text-5xl font-bold bg-gradient-hero bg-clip-text text-transparent tracking-tight mb-6">
                {category.name} Session
              </h1>
              
              <p className="text-xl text-muted-foreground/90 font-light max-w-4xl mx-auto leading-relaxed">
                Select your preferred session type for a personalized {category.name.toLowerCase()} experience.
              </p>
            </div>

            <SessionTypeSelector 
              onSelectType={handleSessionTypeSelection}
              categoryName={category.name}
            />
          </div>
        </div>
      </RoleBasedCategoryAccess>
    );
  }

  // Get theme class based on session type
  const getThemeClass = () => {
    if (!sessionTypeSelected) return '';
    return selectedSessionType === 'individual' ? 'individual-theme' : 'group-theme';
  };

  return (
    <RoleBasedCategoryAccess categoryId={categoryId!}>
      
      <div className="min-h-screen">
        <div className={`transition-all duration-1000 ${getThemeClass()}`}>
          <div className="container mx-auto px-6 py-8 max-w-6xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-12">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              console.log("🔥 Back to Session Type button clicked");
              console.log("🔥 Event:", e);
              console.log("🔥 handleBackToTypeSelection function:", handleBackToTypeSelection);
              e.stopPropagation();
              handleBackToTypeSelection();
              console.log("🔥 handleBackToTypeSelection called");
            }}
            className="rounded-full font-medium text-sm hover:scale-[1.02] transition-all duration-300 relative z-20"
            style={{ pointerEvents: 'auto' }}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Session Type
          </Button>
        </div>

        {/* Category Header */}
        <div className="text-center mb-16 space-y-6">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-primary/20 to-accent/20 border border-primary/30 mb-4">
            <Sparkles className="h-4 w-4 text-primary animate-pulse" />
            <span className="text-sm font-medium text-primary">
              {selectedSessionType === 'individual' ? 'Personalized AI Session' : 'Group AI Session'}
            </span>
          </div>
          
          <div className="text-5xl opacity-90 category-icon mb-6">
            {React.createElement(category.icon, { className: `h-20 w-20 mx-auto ${getCategoryIconColor(categoryId || '')} filter drop-shadow-lg` })}
          </div>
          
          <h1 className="text-5xl font-bold bg-gradient-hero bg-clip-text text-transparent tracking-tight mb-6">
            {selectedSessionType === 'individual' ? 'Individual' : 'Group'} {category.name}
          </h1>
          
          <p className="text-xl text-muted-foreground/90 font-light max-w-4xl mx-auto leading-relaxed mb-8">
            {selectedSessionType === 'individual' 
              ? `A personalized ${category.name.toLowerCase()} experience crafted uniquely for you with advanced AI personalization.`
              : `A shared ${category.name.toLowerCase()} experience designed for your group's collective wellness journey.`
            }
          </p>
        </div>

        {/* Group Session Selector - Only show for group type */}
        {selectedSessionType === 'group' && user && (
          <div className="max-w-2xl mx-auto mb-12">
            <GroupSessionSelector
              value={groupSessionScope}
              onChange={(newScope) => {
                console.log('🎯 GroupSessionSelector onChange:', newScope);
                setGroupSessionScope(newScope);
                
                // Validate group session data when it changes
                if (newScope.sessionScope === 'group' && newScope.groupSession) {
                  const participantCount = newScope.groupSession.participants?.length || 0;
                  const consentingCount = newScope.groupSession.participants?.filter(p => p.consentToName)?.length || 0;
                  console.log('✅ Group session selected:', {
                    id: newScope.groupSession.id,
                    name: newScope.groupSession.group_name,
                    participants: participantCount,
                    consenting: consentingCount
                  });
                  
                  if (consentingCount === 0) {
                    toast({
                      title: "Warning",
                      description: "No group members have consented to name usage. Sessions will use generic language.",
                      variant: "destructive"
                    });
                  }
                }
              }}
            />
          </div>
        )}


        {/* Audio Player */}
        {audioUrl ? (
          <div className="mb-16">
            {/* Session Title */}
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold bg-gradient-hero bg-clip-text text-transparent mb-2">
                {sessionTitle || `${category.name} Session`}
              </h2>
              <p className="text-muted-foreground/80 text-sm">
                Your personalized session is ready to play
              </p>
            </div>

            {currentSessionType && (
              <div className="flex justify-center mb-8">
                <div className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-primary/20 to-accent/20 border border-primary/40 rounded-full text-base font-semibold text-primary backdrop-blur-sm shadow-lg">
                  {currentSessionType === 'sample-basic' && (
                    <>
                      <Play className="h-5 w-5" />
                      Playing: Basic Sample (No Personalization)
                    </>
                  )}
                  {currentSessionType === 'sample-personalized' && (
                    <>
                      <Sparkles className="h-5 w-5 animate-pulse" />
                      Playing: Personalized Sample
                    </>
                  )}
                  {currentSessionType === 'static-sample' && (
                    <>
                      <Sparkles className="h-5 w-5 animate-pulse" />
                      Playing: Curated Sample (Premium Quality)
                    </>
                  )}
                  {currentSessionType === 'template-personalized' && (
                    <>
                      <User className="h-5 w-5" />
                      Playing: Smart Personalized Session
                    </>
                  )}
                  {currentSessionType === 'sample' && (
                    <>
                      <Play className="h-5 w-5" />
                      Playing: Standard Sample
                    </>
                  )}
                   {currentSessionType === 'deeply-personalized' && (
                    <>
                      <Sparkles className="h-5 w-5 animate-pulse" />
                      Playing: Deeply Personalized Session
                    </>
                  )}
                  {!['sample-basic', 'sample-personalized', 'static-sample', 'template-personalized', 'sample', 'deeply-personalized'].includes(currentSessionType) && (
                    <>
                      <Play className="h-5 w-5" />
                      Playing: {currentSessionType}
                    </>
                  )}
                </div>
              </div>
            )}
            
            <div className="border-2 border-border/50 rounded-xl p-4 bg-card/30 hover:bg-card/50 hover:border-border/70 transition-all duration-300">
              {useAudioFallback ? (
                <div className="space-y-4">
                  <div className="text-sm text-orange-600 bg-orange-50 border border-orange-200 rounded-lg p-3">
                    ⚠️ Using fallback audio player due to issues with the enhanced player.
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setUseAudioFallback(false);
                        setAudioPlayerError(null);
                      }}
                      className="ml-2 text-xs"
                    >
                      Try Enhanced Player
                    </Button>
                  </div>
                  <SimpleAudioFallback
                    audioUrl={audioUrl || ''}
                    sessionTitle={sessionTitle || 'Generated Session'}
                    onEnded={() => {
                      console.log('🎵 Session playback ended (fallback)');
                      toast({
                        title: "Session Complete", 
                        description: "How did that feel? Consider writing in your biography to personalize future sessions."
                      });
                    }}
                  />
                </div>
              ) : (
                <div className="space-y-4">
                  {audioPlayerError && (
                    <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
                      ❌ Audio Player Error: {audioPlayerError}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setUseAudioFallback(true);
                          setAudioPlayerError(null);
                        }}
                        className="ml-2 text-xs"
                      >
                        Use Fallback Player
                      </Button>
                    </div>
                  )}
                  <EnhancedAudioPlayer
                    audioUrl={audioUrl}
                    sessionTitle={sessionTitle || 'Generated Session'}
                    backgroundAudioUrl={currentBackgroundMusicFile ? getBackgroundMusicUrl(currentBackgroundMusicFile) : undefined}
                    sessionId={generatedSessionId || undefined}
                    onEnded={() => {
                      console.log('🎵 Session playback ended');
                      toast({
                        title: "Session Complete",
                        description: "How did that feel? Consider writing in your biography to personalize future sessions."
                      });
                    }}
                  />
                </div>
              )}
            </div>

            {/* Generate New Session Button */}
            <div className="flex justify-center mt-8">
              <Button
                variant="outline"
                size="lg"
                onClick={() => {
                  setAudioUrl(null);
                  setSessionTitle('');
                  setGeneratedSessionId(null);
                  setCurrentSessionType(null);
                  setCurrentBackgroundMusicFile(undefined);
                  queueStatus.reset();
                }}
                className="rounded-full px-8 py-3 font-medium hover:scale-[1.02] transition-all duration-300"
              >
                <Sparkles className="h-5 w-5 mr-2" />
                Generate New Session
              </Button>
            </div>
          </div>
        ) : null}


        {/* Main Content - hide when audio is ready */}
        {!audioUrl && (
        <div className="max-w-4xl mx-auto">
          <div className="space-y-6">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold bg-gradient-hero bg-clip-text text-transparent mb-2">Create Your Perfect Session</h3>
                <p className="text-muted-foreground/90 font-light">
                  Generate a fully personalized {category.name.toLowerCase()} experience
                </p>
              </div>

              {user ? (
                <div className="space-y-6">
                  {/* Personalized Generation Card */}
                  <Card className="border-primary/50 bg-card/50 hover:bg-card/80 hover:border-primary/70 transition-all duration-500 shadow-lg hover:shadow-primary/20">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-3 bg-gradient-primary bg-clip-text text-transparent font-bold text-xl">
                        <Sparkles className="h-6 w-6 text-primary animate-pulse" />
                        Personalized {category.name}
                        {selectedSessionType === 'group' && groupSessionScope.groupSession && (
                          <span className="text-xs bg-primary/30 text-primary-foreground px-3 py-1 rounded-full font-semibold">
                            GROUP: {groupSessionScope.groupSession.group_name}
                          </span>
                        )}
                      </CardTitle>
                      <CardDescription className="font-light text-muted-foreground/90 text-base">
                        {selectedSessionType === 'group' && groupSessionScope.groupSession ? (
                          biography.trim() 
                            ? `Personalized for ${groupSessionScope.groupSession.group_name} with ${groupSessionScope.groupSession.participants.length} members`
                            : `Group session for ${groupSessionScope.groupSession.group_name}`
                        ) : (
                          biography.trim()
                            ? `Uses your personal details for a truly customized experience`
                            : `Add your personal details below to generate a customized session`
                        )}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStartPersonalization();
                        }}
                        disabled={isGenerating || (selectedSessionType === 'group' && !groupSessionScope.groupSession)}
                        className="w-full h-16 rounded-xl font-bold text-lg hover:scale-[1.02] transition-all duration-300 relative z-20 shadow-lg hover:shadow-xl"
                        variant="default"
                        size="lg"
                      >
                        {isGenerating ? (
                          <>
                            <Loader2 className="h-5 w-5 mr-3 animate-spin" />
                            Generating Magic...
                          </>
                        ) : (selectedSessionType === 'group' && !groupSessionScope.groupSession) ? (
                          <>
                            <Settings className="h-5 w-5 mr-3" />
                            Select a Group First
                          </>
                        ) : (
                          <>
                            <Sparkles className="h-5 w-5 mr-3 animate-pulse" />
                            {selectedSessionType === 'individual' ? (
                              category.personalizedTemplate ? `Generate Smart Personalized` : `Generate Personalized`
                            ) : (
                              `Generate Group Session`
                            )}
                          </>
                        )}
                      </Button>

                      {/* Queue Status Display - hide if we already have audio (generation complete) */}
                      {!audioUrl && (isGenerating || queueStatus.isQueued || queueStatus.isProcessing) && (
                        <QueueStatusCard
                          status={queueStatus.jobStatus?.status || 'processing'}
                          progress={queueStatus.progress}
                          queuePosition={queueStatus.queuePosition}
                          estimatedTimeRemaining={queueStatus.estimatedTimeRemaining}
                          statusMessage={queueStatus.statusMessage}
                          className="mt-4"
                        />
                      )}
                    
                      {category.personalizedTemplate ? (
                        <div className="p-4 bg-gradient-to-r from-golden-light/20 to-golden-dark/20 rounded-xl border border-golden-light/40 backdrop-blur-sm">
                          <div className="flex items-center gap-3">
                            <User className="h-5 w-5 text-golden-light" />
                            <div>
                              <p className="text-sm font-semibold text-golden-light">Smart Template</p>
                              <p className="text-xs text-muted-foreground/80 font-light">
                                Optimized personalization • {Math.floor((category.personalizedTemplateDuration || 480) / 60)} minutes • Faster generation
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="p-4 bg-gradient-to-r from-golden-light/15 to-golden-dark/15 rounded-xl border border-golden-light/30 backdrop-blur-sm">
                          <div className="flex items-center gap-3">
                            <Settings className="h-5 w-5 text-golden-light" />
                            <div>
                              <p className="text-sm font-semibold text-golden-light">Full Personalization</p>
                              <p className="text-xs text-muted-foreground/80 font-light">
                                Complete custom generation based on your {selectedSessionType === 'individual' ? 'profile' : 'group setup'}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              ) : (
                <Card className="category-card border-golden-light/50 bg-gradient-to-br from-golden-dark/20 to-golden-light/10 hover:border-golden-light/70 transition-all duration-500 shadow-lg shadow-golden-light/20">
                  <CardContent className="p-8 text-center space-y-6">
                    <User className="h-16 w-16 mx-auto text-golden-light" />
                    <div>
                      <h4 className="text-xl font-bold bg-gradient-hero bg-clip-text text-transparent mb-3">Personalized Sessions</h4>
                      <p className="text-muted-foreground/90 font-light mb-6 text-base leading-relaxed">
                        Sign in to create sessions tailored specifically to your preferences and goals.
                      </p>
                      <Button
                        onClick={() => navigate('/auth')}
                        className="rounded-xl font-bold text-lg px-8 py-3 hover:scale-[1.02] transition-all duration-300 shadow-lg"
                        variant="default"
                      >
                        Sign In to Create Custom
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}


            {/* News Input Section - Only for News Meditation */}
            {categoryId === 'news-meditation' && user && (
              <Card className="border-slate-300/30 bg-gradient-to-br from-slate-500/10 to-slate-700/10 hover:border-slate-300/50 transition-all duration-500">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3 text-slate-700 font-bold text-lg">
                    <Newspaper className="h-5 w-5 text-slate-600" />
                    Current News Input (Optional)
                  </CardTitle>
                  <CardDescription className="font-light text-muted-foreground/90">
                    Share specific news that's affecting you for a more targeted meditation
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Textarea
                    value={quickNewsInput}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      if (newValue.length <= 4000) {
                        setQuickNewsInput(newValue);
                        setQuickNewsCharCount(newValue.length);
                      }
                    }}
                    placeholder="Enter the current news or events that are causing you stress or anxiety..."
                    className="min-h-[100px] resize-none"
                    maxLength={4000}
                  />
                  <div className="flex justify-between items-center mt-2">
                    <span className={`text-xs ${
                      quickNewsCharCount > 3600 ? 'text-red-500' :
                      quickNewsCharCount > 3000 ? 'text-yellow-500' :
                      'text-muted-foreground'
                    }`}>
                      {quickNewsCharCount}/4000 characters
                    </span>
                    {quickNewsInput && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setQuickNewsInput('');
                          setQuickNewsCharCount(0);
                        }}
                        className="text-xs text-muted-foreground hover:text-foreground"
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
        )}
        </div>
      </div>

      {/* Background Audio Controls - Hidden since user selects background music in questionnaire */}
      {/* <BackgroundAudioControls /> */}

      {/* Biography Step - shown before questionnaire for individual sessions */}
      {showBiographyStep && (
        <BiographyStep
          biography={biography}
          setBiography={setBiography}
          onContinue={handleBiographyStepContinue}
          onSkip={handleBiographyStepSkip}
          onCancel={handleBiographyStepCancel}
          categoryId={categoryId || ''}
        />
      )}

      {/* Category-Specific Personalization Questionnaire */}
      {showPersonalization && (
        <CategorySpecificPersonalizationQuestionnaire
          categoryName={category.name}
          categoryId={categoryId || ''}
          onSubmit={handlePersonalizationSubmit}
          onCancel={() => {
            console.log("🔥 Questionnaire cancelled - closing modal");
            setShowPersonalization(false);
          }}
          isGenerating={isGenerating}
          sessionType={selectedSessionType}
          isGroupSession={selectedSessionType === 'group'}
          groupName={groupSessionScope.groupSession?.group_name || ''}
        />
      )}
      </div>
    </RoleBasedCategoryAccess>
  );
};

export default CleanCategoryPage;
